import type RankingApiClient from './RankingApiClient'
import type { RunStartPayload, RunEventPayload, RunFinishPayload, BatchSessionPayload } from './RankingApiClient'

const QUEUE_STORAGE_KEY = 'ranking_offline_queue_v1'
const LOCAL_SESSION_PREFIX = 'local-'

type QueuedStartOperation = {
	kind: 'start'
	localSessionId: string
	payload: RunStartPayload
	queuedAtMs: number
}

type QueuedEventOperation = {
	kind: 'event'
	localSessionId: string
	payload: Omit<RunEventPayload, 'run_id'>
	token?: string
	queuedAtMs: number
}

type QueuedFinishOperation = {
	kind: 'finish'
	localSessionId: string
	payload: Omit<RunFinishPayload, 'run_id'>
	token?: string
	queuedAtMs: number
}

type QueuedOperation = QueuedStartOperation | QueuedEventOperation | QueuedFinishOperation

type ResolvedSession = {
	localSessionId: string
	runId: string
	runToken: string
}

const isLocalSessionId = (sessionId: string): boolean => sessionId.startsWith(LOCAL_SESSION_PREFIX)

const createLocalSessionId = (): string => `${LOCAL_SESSION_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const readQueue = (): QueuedOperation[] => {
	try
	{
		const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY)
		if (!raw)
		{
			return []
		}
		const parsed: unknown = JSON.parse(raw)
		return Array.isArray(parsed) ? parsed as QueuedOperation[] : []
	}
	catch
	{
		return []
	}
}

const writeQueue = (queue: QueuedOperation[]) => {
	try
	{
		window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue))
	}
	catch (error)
	{
		// console.warn('[ranking-offline-queue] failed to persist queue', error)
	}
}

const enqueueOperation = (operation: QueuedOperation) => {
	const queue = readQueue()
	queue.push(operation)
	writeQueue(queue)
}

const enqueueStartOperation = (localSessionId: string, payload: RunStartPayload) => {
	enqueueOperation({ kind: 'start', localSessionId, payload, queuedAtMs: Date.now() })
}

const enqueueEventOperation = (localSessionId: string, payload: Omit<RunEventPayload, 'run_id'>, token?: string) => {
	enqueueOperation({ kind: 'event', localSessionId, payload, token, queuedAtMs: Date.now() })
}

const enqueueFinishOperation = (localSessionId: string, payload: Omit<RunFinishPayload, 'run_id'>, token?: string) => {
	enqueueOperation({ kind: 'finish', localSessionId, payload, token, queuedAtMs: Date.now() })
}

const hasQueuedOperations = (): boolean => readQueue().length > 0

const checkConnectivityAndApiAvailability = async (rankingApi: RankingApiClient): Promise<boolean> => {
	if (typeof navigator !== 'undefined' && navigator.onLine === false)
	{
		return false
	}
	return rankingApi.checkHealth()
}

/**
 * Replays every queued start/event/finish call still pending in browser storage
 * as a single batched HTTP request, so a reconnection after a busy offline day
 * does not flood the server with one call per queued item.
 * Returns the sessions whose local id got replaced by a server-issued run id,
 * so the caller can update any in-memory session still pointing to the old id.
 */
const flushQueuedOperations = async (rankingApi: RankingApiClient): Promise<ResolvedSession[]> => {
	const queue = readQueue()
	if (queue.length === 0)
	{
		return []
	}

	const available = await checkConnectivityAndApiAvailability(rankingApi)
	if (!available)
	{
		return []
	}

	const sessionOrder: string[] = []
	const bySession = new Map<string, QueuedOperation[]>()
	for (const operation of queue)
	{
		if (!bySession.has(operation.localSessionId))
		{
			bySession.set(operation.localSessionId, [])
			sessionOrder.push(operation.localSessionId)
		}
		bySession.get(operation.localSessionId)!.push(operation)
	}

	const batchPayload: BatchSessionPayload[] = sessionOrder.map(localSessionId => {
		const operations = bySession.get(localSessionId)!
		const startOp = operations.find((op): op is QueuedStartOperation => op.kind === 'start')
		const eventOps = operations.filter((op): op is QueuedEventOperation => op.kind === 'event')
		const finishOp = operations.find((op): op is QueuedFinishOperation => op.kind === 'finish')

		return {
			local_session_id: localSessionId,
			run_id: startOp ? undefined : localSessionId,
			run_token: startOp ? undefined : (eventOps[0]?.token ?? finishOp?.token),
			start: startOp?.payload,
			events: eventOps.length > 0 ? eventOps.map(op => op.payload) : undefined,
			finish: finishOp?.payload
		}
	})

	let response
	try
	{
		response = await rankingApi.syncBatch(batchPayload)
	}
	catch (error)
	{
		// console.warn('[ranking-offline-queue] batch sync request failed, keeping queue for next attempt', error)
		return []
	}

	const resolved: ResolvedSession[] = []

	for (const localSessionId of sessionOrder)
	{
		const result = response.results.find(item => item.local_session_id === localSessionId)

		if (!result || result.status === 'error')
		{
			// console.warn('[ranking-offline-queue] dropping queued session after server rejection', { localSessionId, message: result?.message })
			continue
		}

		if (result.run_id && result.run_token)
		{
			resolved.push({ localSessionId, runId: result.run_id, runToken: result.run_token })
		}
	}

	writeQueue([])
	return resolved
}

export {
	isLocalSessionId,
	createLocalSessionId,
	enqueueStartOperation,
	enqueueEventOperation,
	enqueueFinishOperation,
	hasQueuedOperations,
	checkConnectivityAndApiAvailability,
	flushQueuedOperations
}
export type { ResolvedSession }
